import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronDown, Home } from 'lucide-react';
import { useAuthStore } from '@shared/stores/authStore';
import { usePropertyStore } from '@shared/stores/propertyStore';
import { fetchMyProperties, type MemberProperty } from '@features/properties/api';
import { cn } from '@shared/lib/cn';

/**
 * Header property chip — frosted trigger with a floating glass popover listing
 * approved properties. Only rendered when the properties module is enabled.
 */
export function PropertySwitcher(): JSX.Element | null {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const memberId = useAuthStore((s) => s.user?.memberId ?? s.user?.member_id);
  const tenantId = useAuthStore((s) => s.tenantId);
  const activePropertyId = usePropertyStore((s) => s.activePropertyId);
  const setActivePropertyId = usePropertyStore((s) => s.setActivePropertyId);
  const syncActiveProperty = usePropertyStore((s) => s.syncActiveProperty);

  const propertiesQuery = useQuery({
    queryKey: ['my-properties', memberId],
    queryFn: fetchMyProperties,
    enabled: !!memberId,
  });

  const properties = propertiesQuery.data ?? [];

  useEffect(() => {
    if (!memberId || !tenantId || properties.length === 0) return;
    syncActiveProperty(properties, { memberId, tenantId });
  }, [properties, memberId, tenantId, syncActiveProperty]);

  const activeProperty = useMemo(() => {
    if (properties.length === 0) return null;
    const match = activePropertyId
      ? properties.find((p) => p.property_id === activePropertyId)
      : undefined;
    return match ?? properties[0];
  }, [properties, activePropertyId]);

  const label = activeProperty?.unit_label ?? 'My residence';
  const canSwitch = properties.length > 1;

  if (propertiesQuery.isLoading) {
    return (
      <span
        className="flex min-w-0 items-center gap-2 rounded-pill bg-white/15 py-1.5 pl-1.5 pr-3 backdrop-blur"
        aria-hidden
      >
        <span className="h-7 w-7 shrink-0 animate-pulse rounded-pill bg-white/25" />
        <span className="h-3 w-20 animate-pulse rounded bg-white/25" />
      </span>
    );
  }

  if (!canSwitch && properties.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate('/app/my-properties')}
        className="flex min-w-0 items-center gap-2 rounded-pill bg-white/15 py-1.5 pl-1.5 pr-3 backdrop-blur transition-colors hover:bg-white/20"
      >
        <PropertyAvatar />
        <span className="truncate text-body-sm font-semibold text-white">Add property</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-white/70" strokeWidth={2} />
      </button>
    );
  }

  if (!canSwitch) {
    return (
      <button
        type="button"
        onClick={() => navigate('/app/my-properties')}
        className="flex min-w-0 max-w-[11rem] items-center gap-2 rounded-pill bg-white/15 py-1.5 pl-1.5 pr-3 backdrop-blur transition-colors hover:bg-white/20"
      >
        <PropertyAvatar />
        <span className="truncate text-body-sm font-semibold text-white">{label}</span>
      </button>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'flex min-w-0 max-w-[11rem] items-center gap-2 rounded-pill py-1.5 pl-1.5 pr-2.5 backdrop-blur transition-colors',
            open ? 'bg-white/25 ring-1 ring-white/30' : 'bg-white/15 hover:bg-white/20',
          )}
        >
          <PropertyAvatar />
          <span className="truncate text-body-sm font-semibold text-white">{label}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-white/80 transition-transform', open && 'rotate-180')}
            strokeWidth={2}
          />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-[60] min-w-[var(--radix-popover-trigger-width)] max-w-[min(18rem,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border border-white/25 bg-black/45 p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <p className="px-3 pb-1 pt-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-white/55">
            Active property
          </p>
          <ul role="listbox" aria-label="Select property" className="max-h-[min(16rem,50dvh)] overflow-y-auto">
            {properties.map((property) => (
              <li key={property.property_id}>
                <PropertyOption
                  property={property}
                  selected={property.property_id === activeProperty?.property_id}
                  onSelect={() => {
                    if (memberId && tenantId) {
                      setActivePropertyId(property.property_id, { memberId, tenantId });
                    } else {
                      setActivePropertyId(property.property_id);
                    }
                    setOpen(false);
                  }}
                />
              </li>
            ))}
          </ul>
          <div className="mt-1 border-t border-white/15 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/app/my-properties');
              }}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-body-sm font-medium text-white/90 transition-colors hover:bg-white/10"
            >
              <Home className="h-4 w-4 shrink-0 text-white/70" strokeWidth={1.75} />
              Manage properties
            </button>
          </div>
          <Popover.Arrow className="fill-black/45" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function PropertyAvatar({ size = 'sm' }: { size?: 'sm' | 'md' }): JSX.Element {
  const dim = size === 'md' ? 'h-8 w-8' : 'h-7 w-7';
  const icon = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-pill bg-white/20 text-white',
        dim,
      )}
      aria-hidden
    >
      <Home className={icon} strokeWidth={2} />
    </span>
  );
}

function PropertyOption({
  property,
  selected,
  onSelect,
}: {
  property: MemberProperty;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const subline = [property.estate_name, property.type_name].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left outline-none transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/25',
        selected ? 'bg-white/18' : 'hover:bg-white/10',
      )}
    >
      <PropertyAvatar size="md" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-sm font-semibold text-white">{property.unit_label}</span>
        {subline ? (
          <span className="mt-0.5 block truncate text-caption text-white/60">{subline}</span>
        ) : property.address ? (
          <span className="mt-0.5 block truncate text-caption text-white/60">{property.address}</span>
        ) : null}
      </span>
      {selected ? <Check className="h-4 w-4 shrink-0 text-brass" strokeWidth={2.5} /> : null}
    </button>
  );
}
